import { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

export const THEMES = {
  meridian: {
    id: 'meridian',
    name: 'Meridian',
    description: 'Стеклянный минимализм',
    hasDarkMode: true,
    preview: {
      light: { bg: '#f2f1ed', surface: '#fbfaf7', accent: '#0a6b5a', text: '#111318', border: 'rgba(17,19,24,0.12)' },
      dark:  { bg: '#0d0f13', surface: '#171a1f', accent: '#21a184', text: '#f2f4f7', border: 'rgba(242,244,247,0.12)' },
    },
  },
  obsidian: {
    id: 'obsidian',
    name: 'Obsidian',
    description: 'Монохромный терминал',
    hasDarkMode: true,
    preview: {
      light: { bg: '#f5f4f0', surface: '#ffffff', accent: '#d97706', text: '#1a1a18', border: 'rgba(26,26,24,0.1)' },
      dark:  { bg: '#0c0c0f', surface: '#111115', accent: '#f59e0b', text: '#e2e4e9', border: 'rgba(255,255,255,0.07)' },
    },
  },
  aurora: {
    id: 'aurora',
    name: 'Aurora',
    description: 'Современный финтех',
    hasDarkMode: true,
    preview: {
      light: { bg: '#f0edf8', surface: '#ffffff', accent: '#7c3aed', text: '#1a1033', border: 'rgba(100,50,180,0.12)' },
      dark:  { bg: '#111827', surface: '#1F2937', accent: '#a78bfa', text: '#F9FAFB', border: 'rgba(167,139,250,0.15)' },
    },
  },
  sandstone: {
    id: 'sandstone',
    name: 'Sandstone',
    description: 'Тёплый редакционный',
    hasDarkMode: true,
    preview: {
      light: { bg: '#faf6ee', surface: '#fffdf7', accent: '#c2714f', text: '#2c2416', border: 'rgba(44,36,22,0.12)' },
      dark:  { bg: '#111827', surface: '#1F2937', accent: '#e8845c', text: '#F9FAFB', border: 'rgba(232,132,92,0.15)' },
    },
  },
  ledger: {
    id: 'ledger',
    name: 'Главная книга',
    description: 'Спокойная финансовая книга',
    hasDarkMode: true,
    preview: {
      light: { bg: '#f3f7f8', surface: '#ffffff', accent: '#087c72', text: '#102a43', border: '#c8dadd' },
      dark:  { bg: '#0b1720', surface: '#12232d', accent: '#5ee0c4', text: '#edf7f8', border: '#29434e' },
    },
  },
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export const ThemeProvider = ({ children }) => {
  const [activeTheme, setActiveTheme] = useState(() => {
    const saved = localStorage.getItem('activeTheme');
    return (saved && THEMES[saved]) ? saved : 'meridian';
  });

  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('isDark') ?? localStorage.getItem('meridianDark');
    if (saved !== null) return saved === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.className = '';
    document.documentElement.classList.add(`theme-${activeTheme}`);
    if (THEMES[activeTheme]?.hasDarkMode && isDark) {
      document.documentElement.classList.add('dark');
    }

    localStorage.setItem('activeTheme', activeTheme);
    localStorage.setItem('isDark', String(isDark));
  }, [activeTheme, isDark]);

  const toggleTheme = () => {
    if (THEMES[activeTheme]?.hasDarkMode) {
      setIsDark(prev => !prev);
    }
  };

  const changeTheme = (themeId) => {
    if (THEMES[themeId]) setActiveTheme(themeId);
  };

  const theme = isDark ? 'dark' : 'light';

  const previewColors = THEMES[activeTheme]?.hasDarkMode && isDark
    ? THEMES[activeTheme].preview.dark
    : THEMES[activeTheme].preview.light;

  return (
    <ThemeContext.Provider value={{
      theme,
      activeTheme,
      isDark: THEMES[activeTheme]?.hasDarkMode ? isDark : false,
      toggleTheme,
      changeTheme,
      themeConfig: THEMES[activeTheme],
      themes: THEMES,
      previewColors,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
